<template>
  <section class="resource-page">
    <div class="resource-toolbar">
      <a-input-search
        v-if="config.searchable"
        v-model:value="query"
        allow-clear
        :placeholder="config.searchPlaceholder"
        @search="reset"
      /><a-select
        v-if="config.statuses"
        v-model:value="status"
        :options="config.statuses"
        @change="reset"
      /><a-button @click="load"><i class="ri-refresh-line" />刷新</a-button>
    </div>
    <a-alert
      v-if="config.hint"
      type="info"
      show-icon
      :message="config.hint"
      class="resource-hint"
    />
    <a-spin :spinning="loading"
      ><a-result v-if="error" status="error" title="数据加载失败" :sub-title="error"
        ><template #extra
          ><a-button type="primary" @click="load">重新加载</a-button></template
        ></a-result
      ><a-table
        v-else
        :data-source="rows"
        :columns="config.columns"
        :pagination="false"
        row-key="id"
        ><template #bodyCell="{ column, record }"
          ><span v-if="column.key === 'date'">{{ date(record[column.dataIndex]) }}</span
          ><a-space v-else-if="column.key === 'membership'" direction="vertical" :size="0"
            ><a-tag>{{ planLabel(record.plan) }}</a-tag
            ><small v-if="record.membership?.expiresAt"
              >到期：{{ date(record.membership.expiresAt) }}</small
            ><small v-if="record.plan === 'team'"
              >{{ record.membership?.seatCount ?? 1 }} 席</small
            ></a-space
          ><a-space v-else-if="column.key === 'quota'" direction="vertical" :size="0"
            ><span
              >Workspace：{{ formatNumber(record.membership?.workspace?.current) }} /
              {{ formatNumber(record.membership?.workspace?.total) }}</span
            ><small
              >Credits（可用）：{{ formatNumber(record.membership?.credits?.available) }} /
              {{ formatNumber(record.membership?.credits?.total) }}</small
            ><small
              >存储（已用）：{{ formatGiB(record.membership?.storage?.used) }} /
              {{ formatGiB(record.membership?.storage?.total) }}</small
            ><small
              >Records（已用）：{{ formatNumber(record.membership?.records?.used) }} /
              {{ formatNumber(record.membership?.records?.total) }}</small
            ></a-space
          ><a-tag v-else-if="column.key === 'status'" :color="statusColor(record.status)">{{
            statusLabel(record.status)
          }}</a-tag
          ><span v-else-if="column.key === 'amount'"
            >¥ {{ (Number(record[column.dataIndex]) / 100).toFixed(2) }}</span
          ><template
            v-else-if="['user', 'owner', 'publisher', 'recipient'].includes(String(column.key))"
            ><a-button
              v-if="userForRecord(record, String(column.key))"
              type="link"
              size="small"
              class="user-link"
              @click.stop="openUserDetail(userForRecord(record, String(column.key)))"
              ><span>{{ userNameForRecord(record, String(column.key)) }}</span
              ><small v-if="column.key !== 'user' || config.endpoint !== '/admin/users'">{{
                userEmailForRecord(record, String(column.key))
              }}</small></a-button
            ><span v-else>已删除用户</span></template
          ><a-tag
            v-else-if="column.key === 'enabled'"
            :color="record.enabled ? 'green' : 'default'"
            >{{ record.enabled ? '已启用' : '已停用' }}</a-tag
          ><code v-else-if="column.key === 'value'">{{ JSON.stringify(record.value) }}</code
          ><a-button v-else-if="column.key === 'fulfillment' && record.status === 'paid_review' && session.can('commerce.manage')" size="small" @click="openAction(record)">对账并确认履约</a-button
          ><a-space v-else-if="column.key === 'action'"
            ><a-button size="small" @click="openAction(record)">{{
              actionText(record.status)
            }}</a-button
            ><a-button v-if="canAdjustCredits" size="small" @click="openCreditAdjustment(record)"
              ><i class="ri-coin-line" />调整积分</a-button
            ></a-space
          ></template
        ></a-table
      >
      <div v-if="config.paginated && !error" class="pagination">
        <a-pagination
          :current="page"
          :page-size="limit"
          :total="total"
          show-size-changer
          @change="changePage"
        />
      </div>
    </a-spin>
  </section>
  <a-modal
    v-model:open="actionOpen"
    :title="actionTitle"
    :confirm-loading="submitting"
    :ok-button-props="{ disabled: !reason.trim() }"
    ok-text="确认提交"
    @ok="submitAction"
  >
    <p>操作原因将写入管理员审计日志。</p>
    <a-alert v-if="config.endpoint === '/admin/commerce/orders'" type="warning" show-icon :message="target?.paymentReview?.reason ?? '已收款待人工核对'" description="请先核对渠道收款与优惠券使用情况。确认后将发放订单权益，并保留管理员和对账原因。" />
    <a-textarea v-model:value="reason" :rows="3" :maxlength="200" show-count />
  </a-modal>
  <a-modal
    v-model:open="creditAdjustmentOpen"
    title="调整用户 Credits"
    :confirm-loading="creditAdjustmentSaving"
    :ok-button-props="{ disabled: !creditAdjustment.credits || !creditAdjustment.reason.trim() }"
    ok-text="确认调整"
    @ok="submitCreditAdjustment"
    ><a-descriptions :column="1" size="small" bordered
      ><a-descriptions-item label="用户">{{
        creditAdjustmentTarget?.displayName ?? '—'
      }}</a-descriptions-item
      ><a-descriptions-item label="当前可用 Credits">{{
        creditAccountLoading
          ? '读取中…'
          : (creditAccount?.availableCredits ?? '无读取权限或读取失败')
      }}</a-descriptions-item></a-descriptions
    ><a-alert
      class="credit-adjustment-note"
      type="warning"
      show-icon
      message="正数增加，负数扣减"
      description="扣减不得使可用余额小于零；服务端会按最早过期优先顺序扣减可用 Credits。" /><a-form
      layout="vertical"
      ><a-form-item label="调整数量（Credits）" required
        ><a-input-number
          v-model:value="creditAdjustment.credits"
          :precision="3"
          :max="1000000"
          :min="-1000000"
          class="full-width" /></a-form-item
      ><a-form-item label="调整原因" required
        ><a-textarea
          v-model:value="creditAdjustment.reason"
          :rows="3"
          :maxlength="200"
          show-count /></a-form-item></a-form
  ></a-modal>
  <AdminUserDetailModal v-model:open="userDetailOpen" :user="userDetailTarget" />
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import { useRoute } from 'vue-router';
import { adminApi } from '@/api';
import AdminUserDetailModal from '@/components/AdminUserDetailModal.vue';
import { useAdminSessionStore } from '@/stores/admin-session.store';

type Column = { title: string; dataIndex?: string; key?: string };
type UserSummary = { id: string; displayName: string; email?: string };
type ResourceConfig = {
  endpoint: string;
  paginated?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  statuses?: Array<{ value: string; label: string }>;
  hint?: string;
  action?: 'user' | 'workspace';
  columns: Column[];
};
const route = useRoute();
const session = useAdminSessionStore();
const loading = ref(false);
const error = ref('');
const rows = ref<Record<string, any>[]>([]);
const query = ref('');
const status = ref('');
const page = ref(1);
const limit = ref(20);
const total = ref(0);
const actionOpen = ref(false);
const target = ref<Record<string, any>>();
const reason = ref('');
const submitting = ref(false);
const creditAdjustmentOpen = ref(false);
const creditAdjustmentSaving = ref(false);
const creditAdjustmentTarget = ref<Record<string, any>>();
const creditAccountLoading = ref(false);
const creditAccount = ref<{
  availableCredits: number;
  reservedCredits: number;
  totalCredits: number;
}>();
const creditAdjustment = reactive({ credits: 0, reason: '' });
const userDetailOpen = ref(false);
const userDetailTarget = ref<UserSummary>();
const configs: Record<string, ResourceConfig> = {
  'admin-users': {
    endpoint: '/admin/users',
    paginated: true,
    searchable: true,
    searchPlaceholder: '按昵称或邮箱搜索',
    action: 'user',
    statuses: [
      { value: '', label: '全部状态' },
      { value: 'active', label: '正常' },
      { value: 'disabled', label: '已停用' },
      { value: 'pendingDeletion', label: '待删除' },
    ],
    columns: [
      { title: '用户', key: 'user' },
      { title: '邮箱', dataIndex: 'email' },
      { title: '套餐', key: 'membership' },
      { title: '额度（当前 / 总量）', key: 'quota' },
      { title: '状态', key: 'status' },
      { title: '注册时间', dataIndex: 'createdAt', key: 'date' },
      { title: '操作', key: 'action' },
    ],
  },
  'admin-workspaces': {
    endpoint: '/admin/workspaces',
    paginated: true,
    searchable: true,
    searchPlaceholder: '按工作台、所有者或 ID 搜索',
    action: 'workspace',
    statuses: [
      { value: '', label: '全部状态' },
      { value: 'active', label: '正常' },
      { value: 'archived', label: '已归档' },
      { value: 'deleting', label: '删除中' },
    ],
    columns: [
      { title: '工作台', dataIndex: 'name' },
      { title: '所有者', key: 'owner' },
      { title: '状态', key: 'status' },
      { title: '更新时间', dataIndex: 'updatedAt', key: 'date' },
      { title: '操作', key: 'action' },
    ],
  },
  'admin-orders': {
    endpoint: '/admin/commerce/orders',
    paginated: true,
    statuses: orderStatuses(),
    columns: [
      { title: '订单号', dataIndex: 'orderNo' },
      { title: '用户', key: 'owner' },
      { title: '金额', dataIndex: 'amountMinor', key: 'amount' },
      { title: '状态', key: 'status' },
      { title: '创建时间', dataIndex: 'createdAt', key: 'date' },
      { title: '对账履约', key: 'fulfillment' },
    ],
  },
  'admin-refunds': {
    endpoint: '/admin/commerce/refunds',
    paginated: true,
    statuses: refundStatuses(),
    columns: [
      { title: '退款单', dataIndex: 'refundNo' },
      { title: '订单号', dataIndex: 'orderNo' },
      { title: '金额', dataIndex: 'amountMinor', key: 'amount' },
      { title: '状态', key: 'status' },
      { title: '创建时间', dataIndex: 'createdAt', key: 'date' },
    ],
  },
  'admin-settlements': {
    endpoint: '/admin/commerce/settlements',
    paginated: true,
    statuses: [
      { value: '', label: '全部状态' },
      { value: 'pending', label: '待结算' },
      { value: 'available', label: '可结算' },
      { value: 'settled', label: '已结算' },
      { value: 'reversed', label: '已冲正' },
    ],
    columns: [
      { title: '发布者', key: 'publisher' },
      { title: '应结算', dataIndex: 'payableAmountMinor', key: 'amount' },
      { title: '佣金', dataIndex: 'commissionAmountMinor', key: 'amount' },
      { title: '状态', key: 'status' },
      { title: '创建时间', dataIndex: 'createdAt', key: 'date' },
    ],
  },
  'admin-payment-attempts': {
    endpoint: '/admin/commerce/payment-attempts',
    paginated: true,
    statuses: [
      { value: '', label: '全部状态' },
      { value: 'created', label: '已创建' },
      { value: 'pending', label: '处理中' },
      { value: 'succeeded', label: '成功' },
      { value: 'failed', label: '失败' },
      { value: 'closed', label: '已关闭' },
    ],
    columns: [
      { title: '商户请求号', dataIndex: 'merchantRequestNo' },
      { title: '渠道', dataIndex: 'channel' },
      { title: '状态', key: 'status' },
      { title: '交易号', dataIndex: 'providerTransactionId' },
      { title: '创建时间', dataIndex: 'createdAt', key: 'date' },
    ],
  },
  'admin-subscriptions': {
    endpoint: '/admin/commerce/subscriptions',
    paginated: true,
    statuses: [
      { value: '', label: '全部状态' },
      { value: 'pending_agreement', label: '待签约' },
      { value: 'scheduled', label: '待生效' },
      { value: 'active', label: '生效中' },
      { value: 'cancel_at_period_end', label: '到期取消' },
      { value: 'cancelled', label: '已取消' },
      { value: 'payment_failed', label: '扣款失败' },
    ],
    columns: [
      { title: '用户', key: 'user' },
      { title: '渠道', dataIndex: 'channel' },
      { title: '状态', key: 'status' },
      { title: '下次扣款', dataIndex: 'nextChargeAt', key: 'date' },
      { title: '到期时间', dataIndex: 'currentPeriodEndsAt', key: 'date' },
    ],
  },
  'admin-admins': {
    endpoint: '/admin/admins',
    columns: [
      { title: '管理员', dataIndex: 'displayName' },
      { title: '邮箱', dataIndex: 'email' },
      { title: '角色', dataIndex: 'roles' },
      { title: '状态', key: 'status' },
      { title: '最近登录', dataIndex: 'lastLoginAt', key: 'date' },
    ],
  },
  'admin-audits': {
    endpoint: '/admin/audits',
    paginated: true,
    columns: [
      { title: '动作', dataIndex: 'action' },
      { title: '目标类型', dataIndex: 'targetType' },
      { title: '目标 ID', dataIndex: 'targetId' },
      { title: '原因', dataIndex: 'reason' },
      { title: '时间', dataIndex: 'createdAt', key: 'date' },
    ],
  },
  'admin-notifications': {
    endpoint: '/admin/notifications',
    paginated: true,
    columns: [
      { title: '标题', dataIndex: 'title' },
      { title: '正文', dataIndex: 'body' },
      { title: '接收者', key: 'recipient' },
      { title: '时间', dataIndex: 'createdAt', key: 'date' },
    ],
  },
  'admin-settings': {
    endpoint: '/admin/settings',
    hint: '配置采用版本化写入；密钥和部署级环境变量不会在后台显示。',
    columns: [
      { title: '配置键', dataIndex: 'key' },
      { title: '当前值', key: 'value' },
      { title: '版本', dataIndex: 'version' },
      { title: '更新时间', dataIndex: 'updatedAt', key: 'date' },
    ],
  },
};
const config = computed(() => configs[String(route.name)] ?? configs['admin-users']);
const canAdjustCredits = computed(
  () => config.value.action === 'user' && session.can('commerce.manage'),
);
const actionTitle = computed(() =>
  config.value.endpoint === '/admin/commerce/orders' ? '人工对账后确认履约' : target.value?.status === 'active' ? '确认变更状态' : '确认恢复状态',
);
function orderStatuses() {
  return [
    { value: '', label: '全部状态' },
    { value: 'pending_payment', label: '待支付' },
    { value: 'paid_review', label: '已收款待对账' },
    { value: 'paid', label: '已支付' },
    { value: 'closed', label: '已关闭' },
    { value: 'refund_pending', label: '退款中' },
    { value: 'refunded', label: '已退款' },
  ];
}
function refundStatuses() {
  return [
    { value: '', label: '全部状态' },
    { value: 'requested', label: '待审核' },
    { value: 'approved', label: '已批准' },
    { value: 'rejected', label: '已拒绝' },
    { value: 'processing', label: '退款中' },
    { value: 'succeeded', label: '已退款' },
    { value: 'failed', label: '失败' },
  ];
}
async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams();
    if (config.value.paginated) {
      params.set('page', String(page.value));
      params.set('limit', String(limit.value));
    }
    if (status.value) params.set('status', status.value);
    if (query.value.trim()) params.set('q', query.value.trim());
    const result = await adminApi.get<any>(
      `${config.value.endpoint}${params.size ? `?${params}` : ''}`,
    );
    if (config.value.endpoint === '/admin/monitor') rows.value = [result.alerts ?? result];
    else if (config.value.paginated) {
      rows.value = result.items;
      total.value = result.total;
      page.value = result.page;
      limit.value = result.limit;
    } else rows.value = result;
  } catch {
    error.value = '请确认管理员权限与后端服务状态后重试。';
  } finally {
    loading.value = false;
  }
}
function reset(): void {
  page.value = 1;
  void load();
}
function changePage(next: number, nextLimit: number): void {
  page.value = next;
  limit.value = nextLimit;
  void load();
}
function userForRecord(record: Record<string, any>, key: string): UserSummary | undefined {
  const candidate =
    key === 'user' && config.value.endpoint === '/admin/users'
      ? record
      : key === 'owner'
        ? (record.owner ?? record.user)
        : record[key];
  return candidate && typeof candidate.id === 'string' && typeof candidate.displayName === 'string'
    ? candidate
    : undefined;
}
function userNameForRecord(record: Record<string, any>, key: string): string {
  return userForRecord(record, key)?.displayName ?? '已删除用户';
}
function userEmailForRecord(record: Record<string, any>, key: string): string {
  return userForRecord(record, key)?.email ?? '';
}
function openUserDetail(user?: UserSummary): void {
  if (!user) return;
  userDetailTarget.value = user;
  userDetailOpen.value = true;
}
function openAction(row: Record<string, any>): void {
  target.value = row;
  reason.value = '';
  actionOpen.value = true;
}
async function openCreditAdjustment(row: Record<string, any>): Promise<void> {
  creditAdjustmentTarget.value = row;
  creditAdjustment.credits = 0;
  creditAdjustment.reason = '';
  creditAccount.value = undefined;
  creditAdjustmentOpen.value = true;
  if (!session.can('commerce.read')) return;
  creditAccountLoading.value = true;
  try {
    creditAccount.value = await adminApi.get(`/admin/users/${row.id}/credits`);
  } catch {
    message.warning('当前 Credits 余额读取失败');
  } finally {
    creditAccountLoading.value = false;
  }
}
async function submitCreditAdjustment(): Promise<void> {
  const target = creditAdjustmentTarget.value;
  const credits = creditAdjustment.credits;
  if (!target || !Number.isFinite(credits) || !credits || !creditAdjustment.reason.trim()) {
    message.warning('请填写非零调整数量和原因');
    return;
  }
  creditAdjustmentSaving.value = true;
  try {
    creditAccount.value = await adminApi.post(`/admin/users/${target.id}/credits/adjustments`, {
      credits,
      reason: creditAdjustment.reason.trim(),
    });
    creditAdjustmentOpen.value = false;
    message.success('Credits 已调整并写入审计日志');
  } catch {
    message.error('积分调整失败：扣减不能使可用余额小于零');
  } finally {
    creditAdjustmentSaving.value = false;
  }
}
async function submitAction(): Promise<void> {
  if (config.value.endpoint === '/admin/commerce/orders') {
    if (submitting.value || !target.value || !reason.value.trim()) return;
    submitting.value = true;
    try {
      await adminApi.post(`/admin/commerce/orders/${target.value.id}/fulfillment-review`, { reason: reason.value.trim() });
      actionOpen.value = false;
      message.success('已确认履约并保存对账记录');
      await load();
    } catch (error: any) { message.error(error?.message ?? '确认履约失败，请刷新后重试'); }
    finally { submitting.value = false; }
    return;
  }
  if (!target.value || !config.value.action || !reason.value.trim()) return;
  submitting.value = true;
  try {
    const active = target.value.status === 'active';
    await adminApi.patch(
      `/admin/${config.value.action === 'user' ? 'users' : 'workspaces'}/${target.value.id}/status`,
      {
        status: active ? (config.value.action === 'user' ? 'disabled' : 'archived') : 'active',
        reason: reason.value.trim(),
      },
    );
    message.success('状态已更新并写入审计日志');
    actionOpen.value = false;
    await load();
  } catch {
    message.error('状态更新失败，请稍后重试');
  } finally {
    submitting.value = false;
  }
}
function date(value: unknown): string {
  return typeof value === 'string'
    ? new Date(value).toLocaleString('zh-CN', { hour12: false })
    : '—';
}
function planLabel(plan: unknown): string {
  return (
    ({ free: 'Free', pro: 'Pro', team: 'Team' } as Record<string, string>)[String(plan)] ?? '—'
  );
}
function formatNumber(value: unknown): string {
  return Number.isFinite(Number(value)) ? Number(value).toLocaleString('zh-CN') : '—';
}
function formatGiB(value: unknown): string {
  return Number.isFinite(Number(value)) ? `${(Number(value) / 1024 ** 3).toFixed(2)} GiB` : '—';
}
function statusLabel(value: unknown): string {
  return (
    (
      {
        active: '正常',
        disabled: '已停用',
        pendingDeletion: '待删除',
        archived: '已归档',
        deleting: '删除中',
        pending_payment: '待支付',
        paid: '已支付',
        closed: '已关闭',
        paid_review: '已收款待对账',
        refund_pending: '退款中',
        refunded: '已退款',
        requested: '待审核',
        approved: '已批准',
        rejected: '已拒绝',
        processing: '处理中',
        succeeded: '成功',
        failed: '失败',
        pending: '待结算',
        available: '可结算',
        settled: '已结算',
        reversed: '已冲正',
      } as Record<string, string>
    )[String(value)] ?? String(value ?? '—')
  );
}
function statusColor(value: unknown): string {
  return (
    (
      {
        active: 'green',
        paid: 'green',
        succeeded: 'green',
        approved: 'green',
        settled: 'green',
        disabled: 'orange',
        archived: 'orange',
        pending: 'orange',
        requested: 'orange',
        pending_payment: 'blue',
        processing: 'blue',
        paid_review: 'orange',
        refund_pending: 'purple',
        refunded: 'cyan',
        pendingDeletion: 'red',
        deleting: 'red',
        failed: 'red',
        rejected: 'red',
        closed: 'default',
      } as Record<string, string>
    )[String(value)] ?? 'default'
  );
}
function actionText(current: unknown): string {
  return current === 'active' ? (config.value.action === 'user' ? '停用' : '归档') : '恢复';
}
watch(
  () => route.name,
  () => {
    query.value = '';
    status.value = '';
    page.value = 1;
    void load();
  },
  { immediate: true },
);
onMounted(() => {
  if (!rows.value.length) void load();
});
</script>

<style scoped>
.resource-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 16px;
}

.resource-toolbar :deep(.ant-input-search) {
  max-width: 310px;
}

.resource-hint {
  margin-bottom: 16px;
}

.resource-page :deep(.ant-table-wrapper),
.resource-page :deep(.ant-spin-container > .ant-result) {
  border: 1px solid #e9eaf2;
  border-radius: 14px;
  background: #fff;
  padding: 4px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.full-width {
  width: 100%;
}

.credit-adjustment-note {
  margin: 16px 0;
}

@media (max-width: 620px) {
  .resource-toolbar {
    flex-wrap: wrap;
    justify-content: stretch;
  }

  .resource-toolbar :deep(.ant-input-search),
  .resource-toolbar :deep(.ant-select) {
    width: 100%;
    max-width: none;
  }

  .resource-toolbar .ant-btn {
    margin-left: auto;
  }
}
</style>
